/**
 * Central Firm Display Formatter — Single Source of Truth
 * =======================================================
 * Converts raw firm objects from /api/firms into standardized,
 * human-readable display values. Pure function, no side effects.
 *
 * Also houses FIRM_BRANDING (static logo/color fallbacks) and
 * buildFirmDataFromApi (eval-type-specific data shaping).
 *
 * Consumes:
 *  - formatFirmPrice() from utils/priceUtils.js
 *  - getDrawdownShort(), getDrawdownLabel() from utils/firmRules.js
 *
 * Every display surface should import from this file instead of
 * deriving display values locally.
 */

import { formatFirmPrice, computeDiscountedPrice, parseActivationFee } from "./priceUtils";
import { getDrawdownShort, getDrawdownLabel } from "./firmRules";
import { estimatePathDays, formatEstimate } from "./pathEstimator";

// ── Firm Branding — visual fallback (logos, colors) ────────────────

export const FIRM_BRANDING = {
  bulenox: {
    logo: "https://bulenox.com/wa-data/public/site/themes/bulenox/img/logo-w-2.png",
    logoAlt: "Bulenox prop firm logo - funded futures trading program",
    brandColor: "#00C853",
    textColor: "#FFFFFF"
  },
  blusky: {
    logo: "https://blusky.pro/_next/static/media/logo.6385efb4.png",
    logoAlt: "BluSky Trading prop firm logo - futures prop trading",
    brandColor: "#43A1F3",
    textColor: "#FFFFFF",
    logoBgLight: true
  },
  "elite-trader-funding": {
    logo: "https://t2.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=http://elitetraderfunding.com&size=128",
    logoAlt: "Elite Trader Funding logo - prop firm for futures traders",
    brandColor: "#10B981",
    textColor: "#FFFFFF"
  },
  "top-one-futures": {
    logo: "https://cdn.prod.website-files.com/67d9f6c73e1490afc415fc90/68e411afc794d45d4f778a78_logo.svg",
    logoAlt: "Top One Futures prop trading firm logo",
    brandColor: "#4CAF50",
    textColor: "#FFFFFF"
  },
  "e8-markets": {
    logo: "https://t2.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=http://e8markets.com&size=128",
    logoAlt: "E8 Markets prop firm logo - futures and forex funded accounts",
    brandColor: "#EC4899",
    textColor: "#FFFFFF"
  },
  "blue-guardian": {
    logo: "https://cdn.prod.website-files.com/67d98b7861a3fdabba993d7d/67d98b7961a3fdabba993db4_Logo%20(74).avif",
    logoAlt: "Blue Guardian prop firm logo - instant funding trading",
    brandColor: "#00A8FF",
    textColor: "#FFFFFF"
  },
  aquafutures: {
    logo: "/logos/aquafutures-logo-color.svg",
    logoAlt: "AquaFutures prop firm logo - instant funded futures trading",
    brandColor: "#0A8FFF",
    textColor: "#FFFFFF",
    logoBgLight: true
  },
  "yrm-prop": {
    logo: "/logos/yrm-favicon.png",
    logoAlt: "YRM Prop firm logo - futures prop trading",
    brandColor: "#00E676",
    textColor: "#FFFFFF",
    logoBgLight: true
  },
  "alpha-futures": {
    logo: "/logos/alpha-futures-favicon.png",
    logoAlt: "Alpha Futures prop firm logo - funded futures trading",
    brandColor: "#8B5CF6",
    textColor: "#FFFFFF",
    logoBgLight: true
  },
  tradeday: {
    logo: "/logos/tradeday-icon.png",
    logoAlt: "TradeDay prop firm logo - funded futures trading with day 1 payouts",
    brandColor: "#00C853",
    textColor: "#FFFFFF",
    logoBgLight: true
  },
  topstep: {
    logo: "/logos/topstep-logo.png",
    logoAlt: "Topstep prop firm logo - funded futures trading",
    brandColor: "#FF6B35",
    textColor: "#FFFFFF"
  },
  apex: {
    logo: "/logos/apex-logo.png",
    logoAlt: "Apex Trader Funding prop firm logo",
    brandColor: "#2563EB",
    textColor: "#FFFFFF"
  }
};

// ── Eval-type-specific data builder ────────────────────────────────

export function buildFirmDataFromApi(firm, evalType = "1-step") {
  if (!firm) return null;
  const isInstant = evalType === "instant-funded";

  // Resolve actual eval-type key in profit_targets (firms use different keys)
  const resolveEvalKey = (obj) => {
    if (!obj) return null;
    if (obj[evalType]) return evalType;
    // Try common aliases
    const keys = Object.keys(obj);
    if (keys.length === 1) return keys[0]; // single key = use it
    return keys.find(k => !["instant-funded", "instant-prime"].includes(k)) || keys[0] || null;
  };

  const profitTargets = {};
  const dailyLoss = {};
  const pricingForType = firm.pricing?.filter(p => p.evaluation_type === evalType) || [];
  // If no pricing for requested evalType, fall back to first available non-instant type
  const actualPricing = pricingForType.length > 0 ? pricingForType :
    (firm.pricing?.filter(p => !["instant-funded", "instant-prime"].includes(p.evaluation_type)) || []);

  const ptKey = resolveEvalKey(firm.profit_targets);
  const dlKey = resolveEvalKey(firm.daily_loss_limits);

  actualPricing.forEach(p => {
    profitTargets[p.size] = isInstant ? "N/A" : (
      firm.profit_targets?.[ptKey]?.[p.size] || firm.profit_targets?.[p.size] || "N/A"
    );
    dailyLoss[p.size] = firm.daily_loss_limits?.[dlKey]?.[p.size] || firm.daily_loss_limits?.[p.size] || "N/A";
  });

  // Resolve path_to_first_payout with same key resolution
  const ptpKey = resolveEvalKey(firm.path_to_first_payout);
  const pathData = firm.path_to_first_payout?.[ptpKey] || firm.path_to_first_payout || {};

  // Extract 50K-specific activation fee from multi-size string
  const extract50kActivation = (feeStr) => {
    if (!feeStr) return "N/A";
    const m50k = feeStr.match(/\$[\d,]+\s*\(50K\)/i);
    if (m50k) return m50k[0].replace(/\s*\(50K\)/i, "");
    // If it's a simple single value, return as-is
    if ((feeStr.match(/\$/g) || []).length <= 1) return feeStr;
    // Multiple values without size labels — return first
    const firstVal = feeStr.match(/\$[\d,]+/);
    return firstVal ? firstVal[0] : feeStr;
  };

  const activation50k = extract50kActivation(firm.activation_fee);

  const pathToFirstPayout = {
    eval: isInstant ? "None" : (pathData.eval || `${firm.min_days} ${firm.min_days === 1 || firm.min_days === "1" ? "day" : "days"}`),
    activation: pathData.activation || activation50k,
    activationRaw: firm.activation_fee || "N/A",
    buffer: isInstant ? "N/A" : (pathData.buffer || actualPricing.find(p => p.size === "$50K")?.buffer || actualPricing[0]?.buffer || "N/A"),
    tradingDays: pathData.trading_days || pathData.tradingDays || firm.funded_min_days || "N/A",
    firstPayout: pathData.first_payout || pathData.firstPayout || firm.first_payout_days || "N/A"
  };

  const pros = firm.pros?.[evalType] || firm.pros?.["1-step"] || (Array.isArray(firm.pros) ? firm.pros : []);
  const cons = firm.cons?.[evalType] || firm.cons?.["1-step"] || (Array.isArray(firm.cons) ? firm.cons : []);

  const brandColor = firm.brand_color || firm.branding?.brand_color || FIRM_BRANDING[firm.slug]?.brandColor || "#3B82F6";

  return {
    profitTargets,
    dailyLoss,
    pros: Array.isArray(pros) ? pros : [],
    cons: Array.isArray(cons) ? cons : [],
    pathToFirstPayout,
    consistencyRule: firm.consistency_rule || "Check firm website for details",
    brandColor,
    platforms: firm.platforms || [],
    payoutMethods: firm.payout_methods || []
  };
}

export function getDisplayData(firm, evalType = "1-step") {
  return buildFirmDataFromApi(firm, evalType);
}

// ── Individual helpers (exported for reuse) ────────────────────────

/**
 * Format min eval days for display.
 * 0, null, undefined → "No minimum"
 */
export function formatMinDays(value) {
  if (value === null || value === undefined || value === 0) return "No minimum";
  return `${value} day${value !== 1 ? "s" : ""}`;
}

/**
 * Short format for min eval days (compact cards / tables).
 * 0, null, undefined → "No min"
 */
export function formatMinDaysShort(value) {
  if (value === null || value === undefined || value === 0) return "No min";
  return `${value}d`;
}

/**
 * Format payout schedule for display.
 * Returns the full payout_frequency string, or "Not specified" for null.
 */
export function formatPayoutSchedule(value) {
  if (!value) return "Not specified";
  return value;
}

/**
 * Abbreviated payout schedule for compact UI.
 * Extracts core cadence from verbose strings.
 */
export function formatPayoutShort(value) {
  if (!value) return "N/A";
  const lower = value.toLowerCase();
  if (lower.startsWith("daily") || lower === "daily") return "Daily";
  if (lower.includes("twice weekly") || lower.includes("2x")) return "2x/wk";
  if (lower.startsWith("weekly") || lower === "weekly") return "1x/wk";
  if (lower.includes("bi-weekly") || lower.includes("biweekly")) return "Bi-weekly";
  return value;
}

/**
 * Format profit split for display.
 * Returns the raw split string (already human-readable), or "See firm page".
 */
export function formatProfitSplit(value) {
  if (!value) return "See firm page";
  return value;
}

/**
 * Format minimum hold time from seconds.
 * 0, null, undefined → "None (Scalp OK)"
 */
export function formatHoldTime(value) {
  if (value === null || value === undefined || value === 0) return "None (Scalp OK)";
  if (value < 60) return `${value} second${value !== 1 ? "s" : ""}`;
  const minutes = Math.round(value / 60);
  return `${minutes} minute${minutes !== 1 ? "s" : ""}`;
}

/**
 * Format rating + review count for display.
 * Returns "4.5 (1,234)" or null if no rating.
 */
export function formatRating(rating, reviewCount) {
  if (rating === null || rating === undefined) return null;
  const r = Number(rating);
  if (isNaN(r)) return null;
  const countStr = reviewCount ? ` (${Number(reviewCount).toLocaleString("en-US")})` : "";
  return `${r}${countStr}`;
}

// ── Discount Validity ───────────────────────────────────────────────

/**
 * Determine if a firm has a valid, active discount right now.
 * Handles both snake_case (/api/firms) and camelCase (/api/firms/discounts) field names.
 * Both conditions required: live flag must be true AND percent must be positive.
 *
 * @param {Object} firm - The firm object from any API endpoint
 * @returns {boolean}
 */
export const isDiscountValid = (firm) => {
  if (!firm) return false;
  const live = firm.is_discount_live === true || firm.isDiscountLive === true;
  const pct = typeof firm.discount_percent === "number" ? firm.discount_percent
            : typeof firm.discountPercent === "number" ? firm.discountPercent
            : 0;
  return live && pct > 0;
};

// ── Main formatter ─────────────────────────────────────────────────

/**
 * Format a raw firm object into display-ready values.
 *
 * @param {Object} firm — raw firm object from /api/firms
 * @param {Object} [pricingItem] — optional specific pricing item (for size-specific display)
 * @returns {Object} Standardized display fields (never undefined/NaN)
 */
export function formatFirmDisplay(firm, pricingItem = null) {
  if (!firm) {
    return {
      name: "Unknown Firm",
      slug: "",
      logo: null,
      priceLabel: "Price",
      priceFormatted: "Contact firm",
      priceRaw: 0,
      priceSuffix: "",
      activationFee: null,
      activationFeeForSize: null,
      activationFeeIsPerSize: false,
      activationFeeRaw: null,
      pricePrimary: "Contact firm",
      pricePrimaryRaw: null,
      priceOriginal: null,
      priceOriginalRaw: null,
      savingsDisplay: null,
      drawdownLabel: "Drawdown",
      drawdownShort: "Unknown",
      drawdownFull: "Unknown Drawdown",
      minEvalDays: "No minimum",
      minEvalDaysShort: "No min",
      payoutSchedule: "Not specified",
      payoutShort: "N/A",
      minHoldTime: "None (Scalp OK)",
      profitSplit: "See firm page",
      ratingDisplay: null,
      discountValid: false,
      discountPercent: null,
      discountDisplay: null,
      discountBadge: false,
      couponCode: null,
    };
  }

  // Pricing — use provided pricing item or first as representative
  const selectedPricing = pricingItem || firm.pricing?.[0] || null;
  const priceFmt = formatFirmPrice(selectedPricing, firm);

  // Activation fee — normalize "None", "$0", "Included", "0" to null
  let activationFee = null;
  if (firm.activation_fee) {
    const af = firm.activation_fee.trim().toLowerCase();
    if (af && af !== "none" && af !== "$0" && af !== "0" && af !== "included" && af !== "n/a") {
      activationFee = firm.activation_fee;
    }
  }

  // Activation fee — per-size parsing
  const afParsed = parseActivationFee(firm.activation_fee, selectedPricing?.size || null);

  // Discount — use isDiscountValid for consistent determination
  const hasDiscount = isDiscountValid(firm);
  const discountPct = hasDiscount
    ? (typeof firm.discount_percent === "number" ? firm.discount_percent : firm.discountPercent)
    : null;

  // Discounted price computation — preserves cadence suffix from the resolved base price
  const discountedRaw = hasDiscount ? computeDiscountedPrice(priceFmt.raw, discountPct) : null;
  const suffix = priceFmt.cadence || "";
  const discountedDisplay = discountedRaw != null ? `$${discountedRaw}${suffix}` : null;
  const savingsRaw = hasDiscount && priceFmt.raw > 0 && discountedRaw != null
    ? priceFmt.raw - discountedRaw
    : null;

  // Coupon code — prefer snake_case field, fall back to camelCase
  const couponCode = hasDiscount
    ? (firm.discount_code || firm.discountCode || null)
    : null;

  return {
    // Identity
    name: firm.name || "Unknown Firm",
    slug: firm.slug || "",
    logo: firm.logo || null,

    // Pricing (base — unchanged)
    priceLabel: priceFmt.label,
    priceFormatted: priceFmt.display,
    priceRaw: priceFmt.raw,
    priceSuffix: suffix,
    activationFee,
    activationFeeForSize: afParsed.feeForSize,
    activationFeeIsPerSize: afParsed.isPerSize,
    activationFeeRaw: afParsed.rawString,

    // Pricing (primary — what the trader pays RIGHT NOW)
    pricePrimary: discountedDisplay || priceFmt.display,
    pricePrimaryRaw: discountedRaw != null ? discountedRaw : priceFmt.raw,
    priceOriginal: hasDiscount ? priceFmt.display : null,
    priceOriginalRaw: hasDiscount ? priceFmt.raw : null,
    savingsDisplay: savingsRaw != null && savingsRaw > 0 ? `$${savingsRaw} saved` : null,

    // Drawdown
    drawdownLabel: "Drawdown",
    drawdownShort: getDrawdownShort(firm),
    drawdownFull: getDrawdownLabel(firm),

    // Eval Requirements
    minEvalDays: formatMinDays(firm.min_days),
    minEvalDaysShort: formatMinDaysShort(firm.min_days),

    // Payout
    payoutSchedule: formatPayoutSchedule(firm.payout_frequency),
    payoutShort: formatPayoutShort(firm.payout_frequency),

    // Hold Time
    minHoldTime: formatHoldTime(firm.min_hold_seconds),

    // Profit Split
    profitSplit: formatProfitSplit(firm.profit_split),

    // Rating
    ratingDisplay: formatRating(firm.verified_trader_rating, firm.verified_trader_rating_count),

    // Discount
    discountValid: hasDiscount,
    discountPercent: discountPct,
    discountDisplay: hasDiscount ? `${discountPct}% OFF` : null,
    discountBadge: hasDiscount,
    couponCode,

    // Path to Payout estimate
    ptpEstimate: estimatePathDays(firm),
    ptpDisplay: formatEstimate(estimatePathDays(firm).total),
  };
}

// ── Firm meta description composer ─────────────────────────────────
/**
 * Compose a firm-specific SEO meta description guaranteed to be 120–155
 * characters. Tries a stack of candidate templates — most informative first,
 * safest/shortest last — and returns the first one that fits the target
 * length band. Falls back to a word-boundary trim only as a last resort.
 *
 * This replaces the previous strict-fallback behaviour where out-of-band
 * descriptions silently collapsed to a single generic template, causing
 * multiple firms to share near-identical meta descriptions.
 *
 * @param {Object} firm — raw firm object (needs `name` minimum)
 * @param {Object} fd — formatted firm display (from formatFirmDisplay)
 * @returns {string} A firm-specific description in 120–155 chars
 */
export function composeFirmMetaDescription(firm, fd) {
  const MIN = 120;
  const MAX = 155;
  const GENERIC_FALLBACK =
    "Compare the best futures prop trading firms (2026). Pricing, drawdown rules, profit splits, payouts, and verified discount codes inside.";

  if (!firm || !firm.name) return GENERIC_FALLBACK;

  const name = firm.name;
  const split = fd?.profitSplit && fd.profitSplit !== "See firm page" ? fd.profitSplit : null;
  const dd = fd?.drawdownFull && fd.drawdownFull !== "Unknown Drawdown" ? fd.drawdownFull : null;
  const payout = fd?.payoutSchedule && fd.payoutSchedule !== "Not specified" ? fd.payoutSchedule : null;

  // Candidate stack — ordered most-informative → universal safety net.
  // Each candidate is firm-specific (mentions firm name at minimum).
  const candidates = [
    // C1: Rich firm data — matches existing passing descriptions verbatim
    split && dd && payout
      ? `${name} review: ${split} split, ${dd}, ${payout} payouts. Pricing, rules, and discounts.`
      : null,
    // C2: Richer 2026-badged variant for when C1 is too short (short firm data)
    split && dd && payout
      ? `${name} review (2026): ${split} split, ${dd} drawdown, ${payout} payouts. Rules, pricing, and current discount codes inside.`
      : null,
    // C3: CTR-focused with firm rule hook (for firms where C1/C2 are too short/long)
    split && dd
      ? `Is ${name} worth it in 2026? Full review with rules, ${dd} drawdown, ${split} split, pricing, payout speed, and discounts.`
      : null,
    // C4: Question-style CTR hook, firm-name only — works for firms with names ≥7 chars
    `Is ${name} worth it in 2026? See rules, drawdown type, pricing, payout speed, and current discounts before you join.`,
    // C5: Universal safety net — always 120–155 for any firm name 2–40 chars long
    `Complete ${name} review (2026) — rules, pricing, drawdown type, payout speed, and current discount codes for futures traders.`,
    // C6: Alt phrasing if C5 somehow fails (extremely long names)
    `${name} review (2026) — full breakdown of rules, pricing, drawdown, payout speed, and current discount codes for futures traders.`,
  ].filter(Boolean);

  for (const c of candidates) {
    if (c.length >= MIN && c.length <= MAX) return c;
  }

  // Last resort — trim the longest firm-specific candidate at word boundary to MAX.
  const longest = candidates.reduce((a, b) => (b.length > a.length ? b : a), candidates[0] || "");
  if (longest.length > MAX) {
    const cut = longest.slice(0, MAX);
    const boundary = cut.lastIndexOf(" ");
    const trimmed = (boundary > MIN ? cut.slice(0, boundary) : cut).replace(/[,.\s]+$/, "") + ".";
    if (trimmed.length >= MIN && trimmed.length <= MAX) return trimmed;
  }
  return longest || GENERIC_FALLBACK;
}
